<template>
  <div class="main-background">
    <InventoryDialog v-model="inventoryDialogVisible" :inventoryDialogData="inventoryDialogData" @save="handleSave"
      @delete="handleDelete" @close="closeInventoryDialog" />
    <CreateStockDialog v-model="createStockDialogVisible" @save="handleCreateDialogSave"
      @close="closeCreateStockDialog" />
    <SearchProductDialog v-model="searchDialogVisible" :stockId="selectedStock" @scan="handleScanData" />

    <v-container v-if="stocks.length !== 0">
      <v-row class="mb-n8">
        <v-col cols="6">
          <v-select v-if="isMobile" v-model="selectedStock" label="Stock" item-title="name" item-value="id"
            :items="stocks" density="compact" variant="solo">

            <template v-slot:append-item>
              <v-divider></v-divider>
              <v-list-item @click="toggleCreateStock">
                <v-list-item-title class="text-primary">
                  + Ajouter un nouveau stock
                </v-list-item-title>
              </v-list-item>
            </template>

          </v-select>
        </v-col>
      </v-row>
      <v-row>

        <v-col :cols="isMobile ? 8 : 7">
          <v-text-field v-model="search" density="compact" label="Search" prepend-inner-icon="mdi-magnify"
            variant="solo-filled" flat single-line></v-text-field>
        </v-col>
        <v-col :cols="isMobile ? 4 : 5" class="d-flex justify-end">
          <v-select v-if="!isMobile" v-model="selectedStock" label="Stock" item-title="name" item-value="id"
            :items="stocks" density="compact" variant="solo">

            <template v-slot:append-item>
              <v-divider></v-divider>
              <v-list-item @click="toggleCreateStock">
                <v-list-item-title class="text-primary">
                  + Ajouter un nouveau stock
                </v-list-item-title>
              </v-list-item>
            </template>

          </v-select>
          <v-btn @click="toggleShowStock" class="mb-5 ml-2" size="small"
            :icon="onlyStock ? 'mdi-pencil' : 'mdi-arrow-left'">
            <v-icon>{{ onlyStock ? 'mdi-pencil' : 'mdi-arrow-left' }}</v-icon>
          </v-btn>
          <v-btn class="mb-5 ml-2" icon="mdi-barcode-scan" size="small" @click="scanProductClick"></v-btn>
        </v-col>
      </v-row>

      <v-data-table-server :headers="headers" :items="filteredProducts" v-model:items-per-page="itemsPerPage"
        :items-length="total" :loading="loading" @update:options="loadProducts">
        <template v-slot:item.unit="{ item }">
          {{ item.unit }}
        </template>
        <template v-slot:item="{ item }">
          <tr @click="handleRowClick(item)">
            <td>{{ item?.productName }}</td>
            <td>{{ item?.unit }}</td>
            <td>{{ item?.totalQuantity }}</td>
          </tr>
        </template>
      </v-data-table-server>
    </v-container>

    <v-container v-else-if="!loading">
      <v-row>
        <v-col class="d-flex justify-center">
          <div>Vous avez encore aucun stock !</div>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="d-flex justify-center">
          <v-btn @click="toggleCreateStock" class="mb-5 ml-2">
            <v-icon class="pr-3">mdi-plus</v-icon>
            Créer un stock
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>





<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue';
import InventoryDialog from "./InventoryDialog.vue";
import SearchProductDialog from "../SearchProductDialog.vue";
import CreateStockDialog from './CreateStockDialog.vue';
import debounce from "lodash/debounce";
import snackbarService from '@/services/snackbarService.js';
import { getProducts, createStock, GetStocks, AddToStock, UpdateStock } from '@/services/stockApi';

const loading = ref(false);
const totalItems = ref(0);
const totalInStock = ref(0);
const itemsPerPage = ref(10);
const search = ref('');
const products = ref([]);
const onlyStock = ref(true);
const inventoryDialogVisible = ref(false);
const inventoryDialogData = reactive({ itemToEdit: {} });
const searchDialogVisible = ref(false);
const createStockDialogVisible = ref(false);
const units = ref([]);
const stocks = ref([]);
const selectedStock = ref(null);

const headers = [
  { title: "Nom", key: "productName", sortable: false },
  { title: "Unité", key: "unit", sortable: false },
  { title: "Quantité", key: "totalQuantity", sortable: false },
];

const isMobile = computed(() => window.innerWidth < 600); // Ajuster le breakpoint si nécessaire
const filteredProducts = computed(() => {
  if (onlyStock.value && products.value != null) {
    return products.value.filter(item => item.totalQuantity > 0);
  }
  return products.value;
});
const total = computed(() => (onlyStock.value ? totalInStock.value : totalItems.value));

watch(search, debounce(() => loadProducts(), 700));
watch(selectedStock, () => loadProducts());

const loadProducts = async (options = {}) => {
  try{
    const { page = 1, itemsPerPage = 10 } = options;
    var data = await getProducts(selectedStock.value,page,itemsPerPage,search.value);

    products.value = data.userStock;
    totalItems.value = data.pagination.totalProducts;
    totalInStock.value = data.pagination.totalInStockQuantity;
  }
  catch(error){
    snackbarService.showSnackbar("Une erreur est survenue lors de la récupération des données.", "red")
  }
};

const handleRowClick = (item) => {
  inventoryDialogData.itemToEdit = { ...item };
  inventoryDialogVisible.value = true;
};

const closeInventoryDialog = () => {
  inventoryDialogVisible.value = false;
  Object.assign(inventoryDialogData.itemToEdit, {}); // Réinitialisation réactive
};

const handleCreateDialogSave = async (stockName) => {
  try {
    await createStock(stockName);
    snackbarService.showSnackbar("Stock créé avec succès !", "green");
    createStockDialogVisible.value = false;
    await getStocks();

  } catch (ex) {
    snackbarService.showSnackbar(errorMessage, "red");
  }
};

const getStocks = async () => {
  loading.value = true;
  var response = await GetStocks();
  stocks.value = response.data;
  loading.value = false;
};

const handleSave = async (updatedItem) => {
  try {
    const newStocks = updatedItem.items.filter(stock => stock.id === 0).map(stock => ({
      stockId: selectedStock.value,
      productId: stock.productId,
      quantity: stock.quantity,
      date: stock.expirationDate,
    }));
    
    const updatedStocks = updatedItem.items.filter(stock => stock.id).map(stock => ({
      id: stock.id,
      quantity: stock.quantity,
      ExpirationDate: stock.expirationDate,
    }));

    await AddToStock(newStocks);
    await UpdateStock(updatedStocks);

    snackbarService.showSnackbar("Les produits ont été mis à jour avec succès !", 'success');
    loadProducts();
  } catch (error) {
    console.error("Error saving product:", error);
    snackbarService.showSnackbar(error.message, 'error');
  } finally {
    inventoryDialogVisible.value = false;
  }
};

const toggleShowStock = () => {
  onlyStock.value = !onlyStock.value;
  loadProducts();
};

const toggleCreateStock = () => {
  createStockDialogVisible.value = true;
};

const closeCreateStockDialog = () => {
  createStockDialogVisible.value = false;
};

const handleScanData = async (data) => {
  searchDialogVisible.value = false;
  inventoryDialogData.itemToEdit = { ...data };
  inventoryDialogVisible.value = true;
};

const scanProductClick = () => {
  searchDialogVisible.value = true;
};

onMounted(async() => {
  loading.value = true;
  //await loadProducts();
  await getStocks();

  if (stocks.value.length > 0) {
    selectedStock.value = stocks.value[0].id;
  }
});

</script>

<style scoped>
.dropdown-container {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
}

.hover-highlight tbody tr:hover {
  background-color: #e0e0e0;
}
</style>
